import { useApolloClient, useMutation } from '@apollo/client'
import React from 'react'
import { toEuroString } from '../../../common/utils'
import { User } from '../../auth/types'
import { i18n } from '../../index/index'
import { MonthlyTargetsEditor } from '../monthlyTargets/MonthlyTargetsEditor'
import { MonthlyTargetsFormData, MonthlyTargetValue } from '../monthlyTargets/types'
import { createValueFormat } from '../monthlyTargets/valueFormat'
import {
  fetchStationSalesIncentiveProducts,
  getMonthlyTargetsByBudget,
  upsertIncentiveSalesTargetMutation,
} from './incentiveProductQueries'
import { IncentiveSalesTarget, MonthlySalesIncentive, MonthlyTarget } from './incentiveProductTypes'

interface IncentiveSalesTargetsEditorProps {
  date?: Date
  stationId: string
  user?: User
  incentiveSalesTargetData?: IncentiveSalesTarget
  lastYearOutcomeData?: MonthlySalesIncentive[]
}

const eurosFormat = createValueFormat((value) => toEuroString(value) ?? '')

const toTargetValues = (targets?: MonthlyTarget[] | null): MonthlyTargetValue[] =>
  (targets ?? []).map((t) => ({ month: t.month, year: t.year, value: t.incentiveSalesTarget }))

/** Incentive sales wrapper around the shared monthly targets editor. Values are euros. */
export function IncentiveSalesTargetsEditor({
  date,
  stationId,
  user,
  incentiveSalesTargetData,
  lastYearOutcomeData,
}: IncentiveSalesTargetsEditorProps) {
  const client = useApolloClient()
  const [upsertIncentiveSalesTarget] = useMutation(upsertIncentiveSalesTargetMutation, {
    refetchQueries: [fetchStationSalesIncentiveProducts, 'FetchStationSalesIncentiveProducts'],
    fetchPolicy: 'network-only',
  })

  if (!user) {
    return null
  }

  const fetchTargetsByBudget = async (year: number, yearlyTarget: number): Promise<MonthlyTargetValue[]> => {
    const { data } = await client.query<
      { monthlyTargetsByBudget: MonthlyTarget[] },
      { stationId: string; year: number; yearlyTarget: number }
    >({
      query: getMonthlyTargetsByBudget,
      variables: { stationId, year, yearlyTarget },
      fetchPolicy: 'network-only',
    })
    return toTargetValues(data.monthlyTargetsByBudget)
  }

  const save = (data: MonthlyTargetsFormData) =>
    upsertIncentiveSalesTarget({
      variables: {
        stationId,
        data: {
          date,
          incentiveSalesTarget: data.yearlyTarget,
          shiftPercentages: data.shiftPercentages,
          monthlyTargets: data.monthlyTargets.map((mt) => ({
            month: mt.month,
            year: mt.year,
            incentiveSalesTarget: mt.value,
          })),
        },
      },
    })

  return (
    <MonthlyTargetsEditor
      date={date}
      yearlyTarget={incentiveSalesTargetData?.incentiveSalesTarget}
      monthlyTargets={toTargetValues(incentiveSalesTargetData?.monthlyTargets)}
      shiftPercentages={incentiveSalesTargetData?.shiftPercentages}
      lastYearActuals={lastYearOutcomeData?.map((m) => ({ month: m.month, value: m.totalIncentiveSales }))}
      labels={{
        title: i18n.t('admin.incentiveSalesTargets.annualTarget'),
        divideByBudget: i18n.t('admin.incentiveSalesTargets.divideYearlyTargetByBudget'),
        monthlyTargetsHeading: i18n.t('admin.incentiveSalesTargets.monthlyTargetsHeadingText'),
        updateError: i18n.t('admin.incentiveSalesTargets.updateError'),
      }}
      valueFormat={eurosFormat}
      canEditYearlyTarget={user.userType === 'SokAdmin'}
      editRequiresYearlyTarget={!!date && date.getFullYear() > new Date().getFullYear()}
      canUseDivideByBudget={user.userType === 'SokAdmin'}
      fetchTargetsByBudget={fetchTargetsByBudget}
      save={save}
    />
  )
}
